import baseUrl from '../../api/baseUrl'
import type { LabOrderDetail } from './labOrderTypes'

/** Same cap the backend enforces per order (clinic and lab photos together). */
export const MAX_LAB_ORDER_PHOTOS = 20

/** Long edge after resizing — plenty for case photos, keeps uploads small. */
const MAX_PHOTO_EDGE = 2000
const PHOTO_JPEG_QUALITY = 0.85

export const UNSUPPORTED_PHOTO_MESSAGE =
  "This photo format isn't supported. Try a JPEG or PNG."

/** A photo picked before the order exists; uploaded right after create. */
export type DraftLabOrderPhoto = {
  localId: string
  dataUrl: string
}

export function newLocalPhotoId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

type DecodedImage = {
  source: CanvasImageSource
  width: number
  height: number
  release: () => void
}

function decodeWithImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () =>
      resolve({
        source: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
        release: () => URL.revokeObjectURL(objectUrl),
      })
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error(UNSUPPORTED_PHOTO_MESSAGE))
    }
    img.src = objectUrl
  })
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      // from-image applies the EXIF rotation phone cameras write. Cast because
      // TypeScript 4.9's DOM types predate that value.
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      } as unknown as ImageBitmapOptions)
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      }
    } catch {
      // Older Safari rejects the options or the blob — try an <img> instead.
    }
  }
  return decodeWithImageElement(file)
}

/**
 * Decodes a picked photo, scales it so the long edge is at most 2000px
 * (never upscaled) and returns it as a JPEG data URL ready for upload.
 */
export async function prepareLabOrderPhoto(file: File): Promise<string> {
  const image = await decodeImage(file)
  try {
    if (!image.width || !image.height) {
      throw new Error(UNSUPPORTED_PHOTO_MESSAGE)
    }
    const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(image.width, image.height))
    const width = Math.max(1, Math.round(image.width * scale))
    const height = Math.max(1, Math.round(image.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not process this photo. Please try again.')
    // JPEG has no transparency — paint white so transparent PNGs don't turn black.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(image.source, 0, 0, width, height)

    const dataUrl = canvas.toDataURL('image/jpeg', PHOTO_JPEG_QUALITY)
    if (!dataUrl.startsWith('data:image/')) {
      throw new Error('Could not process this photo. Please try again.')
    }
    return dataUrl
  } finally {
    image.release()
  }
}

export function labOrderPhotoErrorMessage(err: unknown, fallback: string) {
  const ax = err as { response?: { data?: unknown }; message?: string }
  const data = ax?.response?.data
  if (data && typeof data === 'object' && 'message' in data) {
    const msg = (data as { message?: string }).message
    if (msg) return msg
  }
  if (typeof data === 'string' && data.trim()) return data
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function photosPath(orderId: string) {
  return `client/labs/orders/${encodeURIComponent(orderId)}/photos`
}

/** Uploads one photo to an existing order (by Mongo `_id`); returns the updated detail. */
export async function addLabOrderPhoto(orderId: string, dataUrl: string) {
  const res = await baseUrl.post(photosPath(orderId), { image: dataUrl })
  return res.data.data as LabOrderDetail
}

export async function deleteLabOrderPhoto(orderId: string, photoId: string) {
  const res = await baseUrl.delete(
    `${photosPath(orderId)}/${encodeURIComponent(photoId)}`,
  )
  return res.data.data as LabOrderDetail
}

/**
 * Uploads photos picked in a create form to the order that was just created,
 * one at a time. Never throws — the order already exists, so the caller just
 * reports how many failed.
 */
export async function uploadDraftLabOrderPhotos(
  orderId: string | null | undefined,
  photos: DraftLabOrderPhoto[],
  onProgress?: (current: number, total: number) => void,
) {
  if (!photos.length) return 0
  if (!orderId) return photos.length
  let failed = 0
  for (let i = 0; i < photos.length; i++) {
    onProgress?.(i + 1, photos.length)
    try {
      await addLabOrderPhoto(orderId, photos[i].dataUrl)
    } catch {
      failed++
    }
  }
  return failed
}

export function draftPhotoFailureMessage(failed: number) {
  return `Order created, but ${failed} photo${failed === 1 ? '' : 's'} failed to upload. Add ${failed === 1 ? 'it' : 'them'} again from the order.`
}
